/** @jsx CanvaSX */
import { CanvaSX } from '@lib/canvasx/canvasx';
import { Rect } from '@lib/canvasx/components/Rect';
import { canvasCssValue } from '@lib/canvasx/util';
import { Style } from '@lib/canvasx/components/Style';
import { FlexColumn } from '@lib/canvasx/components/FlexColumn';
import { type RenderedDimensions } from '@lib/canvasx/components/FlexRow';
import Color from 'color';

export type RecordCardProps = {
  backgroundColor?: string;
  highlightColor?: string;
  setRenderedDimensions?: (dimensions: RenderedDimensions) => void;
  opacity?: number;
} & ShapeStruct;

export const CARD_BORDER_RADIUS = 5;
export const CARD_PADDING: SpacingAxisStruct = { horizontal: 9, vertical: 5 };

export const availableContentWidth = (width: number) =>
  width - CARD_BORDER_RADIUS - CARD_PADDING.horizontal * 2;

export const availableContentHeight = (height: number) =>
  height - CARD_PADDING.vertical * 2;

const RecordCard: CanvaSXFC<RecordCardProps> = function ({
  backgroundColor,
  highlightColor,
  width,
  height,
  children,
  setRenderedDimensions,
  opacity = 1,
}) {
  const themeBackground = canvasCssValue(
    '--theme-secondary-background',
    '#ffffff'
  );

  const bgFill = backgroundColor
    ? Color(backgroundColor).mix(Color(themeBackground), 0.9).hex()
    : themeBackground;

  const highlightFill = highlightColor || backgroundColor;

  const contentWidth = highlightFill ? width - CARD_BORDER_RADIUS : width;

  return (
    <Style
      fill={{
        color: canvasCssValue('--theme-tertiary-text', '#999999'),
      }}
    >
      <Rect
        borderRadius={CARD_BORDER_RADIUS}
        border={canvasCssValue('--theme-primary-border', '#e1e1e1')}
        fill={{ color: bgFill, opacity }}
        width={width}
        height={height}
      />

      {highlightFill ? (
        <Rect
          x={0.5} // dont overlap border
          width={CARD_BORDER_RADIUS * 2}
          height={height}
          borderRadius={CARD_BORDER_RADIUS}
          fill={{ color: highlightFill, opacity }}
          clip={{ x: 0, y: 0, width: CARD_BORDER_RADIUS, height }}
        />
      ) : null}

      <FlexColumn
        x={highlightFill ? CARD_BORDER_RADIUS : 0}
        width={contentWidth}
        padding={CARD_PADDING}
        clip={{
          x: 0,
          y: 0,
          width,
          height,
        }}
        setRenderedDimensions={setRenderedDimensions}
        horizontalAlignment='stretch'
      >
        {children}
      </FlexColumn>
    </Style>
  );
};

export default RecordCard;
